import React, { Fragment, useEffect, useState } from "react";
import qs from "qs";
import { graphql, Link } from "gatsby";
import {
  Button,
  Icon,
  Header,
  Label,
  Message,
  List,
  Divider,
} from "semantic-ui-react";
import { FormattedMessage, injectIntl } from "react-intl";
import { useLocation } from "@reach/router";
import axios from "axios";
import Layout from "../components/layout";
import { Author } from "../components/author";
import renderAst from "../utils/ast-compiler";
import { I18Link } from "../components/i18link";
import { ReleaseImageIntl } from "../components/release";
import { FormattedDate } from "../components/date";
import filterReleasePosts from "../../lib/release-post-adapters";

const AllReleasesMenuItem = injectIntl(({ isLatest, intl }) => {
  if (isLatest) {
    return (
      <Fragment>
        {intl.formatMessage({
          id: "release.menu.older-releases",
        })}
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        {intl.formatMessage({
          id: "release.menu.all-releases",
        })}
      </Fragment>
    );
  }
});

const fetchDownloadUri = (version) => {
  const uri = "https://www.screenpresso.com/version-history.json";
  return axios
    .get(uri + qs.stringify({ type: "stable" }, { addQueryPrefix: true }))
    .then(({ data }) => {
      const pattern = "^" + version.replace(/\./g, "\\.");
      const re = new RegExp(pattern, "i");
      const filtered = data.filter((item) => re.test(item.version));
      if (filtered.length === 0) {
        return null;
      }

      return filtered[0];
    })
    .catch((error) => {
      // Do not throw
      console.error(error.message);
      return null;
    });
};

const ReleasePage = ({
  pageContext: { language, title, id, featuredImage, canonicalpath },
  data,
  intl,
}) => {
  const [releases, setReleases] = useState([]);
  const [releaseInfo, setReleaseInfo] = useState({});
  const location = useLocation();

  const { from } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { post, allPosts } = data;

  const isSummary = location ? /summary/.test(location.pathname) : false;

  const { version } = post.frontmatter;

  // Use effect otherwise reconcialiation issues occur
  useEffect(() => {
    setReleases(filterReleasePosts(allPosts, post, from));
  }, [allPosts, post, from]);

  useEffect(() => {
    fetchDownloadUri(version).then((data) => {
      setReleaseInfo(data);
    });
  }, [version]);

  const isLatest = post.frontmatter.tags.includes("latest");

  return (
    <Layout
      locale={language}
      title={title}
      pageId={id}
      featuredImage={featuredImage}
      canonicalpath={canonicalpath}
      description={post.excerpt}
    >
      {!isSummary && (
        <Header as="h1">
          <Header.Content>{post.frontmatter.title}</Header.Content>
          {isLatest && (
            <Label color="green">
              <FormattedMessage id="release.tags.latest" />
            </Label>
          )}
          <Header.Subheader>
            <List divided horizontal link>
              <List.Item>
                <I18Link id="download" autotext icon="left arrow" />
              </List.Item>
              <List.Item>
                <I18Link id="releases" icon="list alternate outline">
                  <span style={{ paddingLeft: "0.8rem" }}>
                    <AllReleasesMenuItem isLatest={isLatest} />
                  </span>
                </I18Link>
              </List.Item>
            </List>
          </Header.Subheader>
        </Header>
      )}

      <div style={{ width: "80%", margin: "auto", marginBottom: "2rem" }}>
        {/* No SVG for summary */}
        {isSummary ? (
          <img style={{ width: "100%" }} src={featuredImage} alt="Release" />
        ) : (
          <ReleaseImageIntl
            date={post.frontmatter.date}
            locale={language}
            version={post.frontmatter.version}
          />
        )}
      </div>

      {!isSummary && (
        <Header as="h4">
          <Author.Image id={post.frontmatter.author} />
          <Header.Content>
            <Author.Text id={post.frontmatter.author} />
            <Header.Subheader>
              <FormattedDate locale={language} value={post.frontmatter.date} />
            </Header.Subheader>
          </Header.Content>
        </Header>
      )}

      {releases.length > 0 && (
        <h4 className={isSummary ? "muted" : ""}>
          {post.frontmatter.version}{" "}
        </h4>
      )}

      <div>{renderAst(post.htmlAst)}</div>

      {releases.map((olderRelease) => (
        <div key={olderRelease.fields.id}>
          <Divider style={{ marginTop: "25px", marginBottom: "25px" }} />
          <h4 className="muted">{olderRelease.frontmatter.version}</h4>
          {renderAst(olderRelease.htmlAst)}
        </div>
      ))}
      <p />
      {!isSummary && (
        <>
          <Divider style={{ marginTop: "25px", marginBottom: "25px" }} />
          {isLatest ? (
            <div>
              <Button
                labelPosition="left"
                size="medium"
                color="red"
                icon
                as={I18Link}
                id="download"
              >
                <Icon name="arrow down" />
                <FormattedMessage id="release.download-button" />
              </Button>
              <strong>
                <FormattedMessage
                  id="release.update.alternative"
                  values={{
                    checkForUpdate: (
                      <Button
                        size="medium"
                        color="grey"
                        as={I18Link}
                        id="how-to-update-latest"
                      >
                        <FormattedMessage id="release.update.label" />
                      </Button>
                    ),
                  }}
                />
              </strong>
            </div>
          ) : (
            <Fragment>
              <Message icon>
                <Icon name="warning sign" color="red"/>
                <Message.Content>
                  <Message.Header>
                    <FormattedMessage id="release.new-version-warning.header" />
                  </Message.Header>

                  <Link to={data.latestReleaseNoteLink.edges[0].node.path}>
                    <strong>
                      <FormattedMessage
                        id="release.new-version-warning.content"
                        values={{
                          version: data.latest.frontmatter.version,
                        }}
                      />
                    </strong>
                  </Link>
                </Message.Content>
              </Message>
              {releaseInfo && releaseInfo.url && (
                <Button
                  labelPosition="left"
                  size="medium"
                  color="gray"
                  icon
                  as={"a"}
                  href={releaseInfo.url}
                >
                  <Icon name="arrow down" />
                  <FormattedMessage id="release.download-button" />
                  &nbsp;v{releaseInfo.version}&nbsp;-&nbsp;EXE
                </Button>
              )}

              {releaseInfo && releaseInfo.urlMsi && (
                <Button
                  labelPosition="left"
                  size="medium"
                  basic
                  color="gray"
                  icon
                  as={"a"}
                  href={releaseInfo.urlMsi}
                >
                  <Icon name="arrow down" />
                  
                  &nbsp;v{releaseInfo.version}&nbsp;-&nbsp;MSI
                </Button>
              )}
            </Fragment>
          )}
        </>
      )}
    </Layout>
  );
};

export default ReleasePage;

export const query = graphql`query ReleasePageQuery($slug: String!, $language: String!) {
  post: markdownRemark(fields: {slug: {eq: $slug}}) {
    htmlAst
    excerpt
    frontmatter {
      title
      date
      tags
      author
      version
    }
    fields {
      id
    }
  }
  allPosts: allMarkdownRemark(
    filter: {frontmatter: {tags: {eq: "release"}}}
    sort: {frontmatter: {date: DESC}}
  ) {
    edges {
      node {
        htmlAst
        frontmatter {
          title
          date
          tags
          author
          version
        }
        fields {
          id
        }
      }
    }
  }
  latest: markdownRemark(frontmatter: {tags: {eq: "latest"}}) {
    frontmatter {
      version
      date
    }
  }
  latestReleaseNoteLink: allSitePage(
    filter: {context: {tags: {eq: "latest"}, language: {eq: $language}}}
  ) {
    ...LanguageMap
  }
}`;
